import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, RotateCcw } from "lucide-react";

/**
 * PermissionsMatrix — grid of on/off toggles by permission key.
 * Fetches the catalog + role defaults, merges with user overrides,
 * lets caller PATCH the final list.
 */
export default function PermissionsMatrix({ open, onClose, user, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      try {
        const { data } = await api.get("/permissions/definitions");
        setCatalog(data.catalog);
        setRoleDefaults(data.role_defaults);
        // Merge role defaults with user override
        const roleKeys = new Set(data.role_defaults[user.role] || []);
        const overrides = new Set(user.permissions || []);
        setSelected(new Set([...roleKeys, ...overrides]));
      } catch (e) {
        toast.error("Failed to load permissions");
      }
    })();
  }, [open, user]);

  const roleKeys = useMemo(() => new Set(roleDefaults[user?.role] || []), [roleDefaults, user]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const resetToRole = () => {
    setSelected(new Set(roleKeys));
  };

  const save = async () => {
    setBusy(true);
    try {
      // Store only DELTAS from role defaults so future role changes still make sense.
      // We send full list (server merges anyway); UI is additive granted set.
      await api.patch(`/permissions/user/${user.id}`, { permissions: Array.from(selected) });
      toast.success("Permissions updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="permissions-matrix-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-brand" /> Permissions · {user?.full_name || user?.username}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Role: <span className="font-mono">{user?.role}</span>. Toggle individual permission keys. Grayed keys are role defaults.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {catalog.map((group) => (
            <div key={group.group} className="border border-border rounded-xl p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{group.group}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {group.items.map((item) => {
                  const isRoleDefault = roleKeys.has(item.key);
                  const isSelected = selected.has(item.key);
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => toggle(item.key)}
                      className={`text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg border transition-colors ${
                        isSelected
                          ? "border-emerald-brand/40 bg-emerald-brand/8"
                          : "border-border bg-muted/30 hover:bg-muted/60"
                      }`}
                      data-testid={`perm-toggle-${item.key}`}
                    >
                      <span
                        className={`h-3.5 w-3.5 rounded-md grid place-items-center border ${
                          isSelected ? "bg-emerald-brand text-white border-emerald-brand" : "border-muted-foreground/40"
                        }`}
                      >
                        {isSelected ? "✓" : ""}
                      </span>
                      <span className="text-sm">{item.label}</span>
                      {isRoleDefault && <span className="ml-auto text-[9px] font-mono uppercase text-muted-foreground">role</span>}
                      <span className="text-[10px] font-mono text-muted-foreground">{item.key}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={resetToRole} className="rounded-xl" data-testid="perm-reset-button">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Reset to role defaults
          </Button>
          <Button onClick={save} disabled={busy} className="rounded-xl shadow-md shadow-primary/20" data-testid="perm-save-button">Save permissions</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
